import React, { useEffect, useState, useMemo } from "react";
import { Box, Stack, Backdrop, CircularProgress } from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { GridColDef, GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid";

// Components
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzNurseDeleteButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseDeleteButton";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";


// Internal Refactored Components
import PartographBornInfo from "./components/PartographBornInfo";
import PartographTraceGrid from "./components/PartographTraceGrid";
import PartographParameters from "./components/PartographParameters";
import PartographCharts from "./components/PartographCharts";
import PartographBottomGrids from "./components/PartographBottomGrids";
import PartographDialogs from "./components/PartographDialogs";

// Utils & Hooks
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { usePartographForm } from "../hooks/usePartographForm";
import { referenceService } from "src/api/references/referenceService";
import { nurseService } from "src/api/nurse/nurseService";
import { ConnectionOfMedcardResponseDto } from "src/api/references/referenceServicesDtos";
import { formatTime, formatDate, formatDateTime } from "./components/utils";

interface PartografFormProps {
  onCloseModal?: () => void;
  tabId?: string;
}

function PartografForm({ onCloseModal, tabId }: PartografFormProps) {
  const { t } = useTranslate();

  const { user } = useAppSelector((state) => state.auth);
  const { id: patientIdParam } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const orId = Number(searchParams.get("orId")) || 0;
  const patientId = Number(patientIdParam) || 0;

  const [comId, setComId] = useState<number | null>(null);

  // Medcard bağlantısından COM_ID üret
  useEffect(() => {
    const fetchComId = async () => {
      if (!patientId || !orId) return;
      try {
        const response = (await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
          pt: patientId,
          orId,
        })) as ConnectionOfMedcardResponseDto;

        let _comId =
          response.data && response.data.length > 0
            ? response.data.sort((a, b) => b.comid - a.comid)[0].comid
            : 0;

        if (!_comId) {
          const createResponse = await referenceService.connectionsOfMedcard.createConnectionOfMedCard({
            pt_id: patientId,
            or_id: orId,
          });

          if (createResponse?.comid) {
            _comId = createResponse.comid;
          } else if (createResponse?.data !== undefined) {
            if (typeof createResponse.data === "number") {
              _comId = createResponse.data;
            } else if (Array.isArray(createResponse.data) && createResponse.data.length > 0) {
              const retryResponse = (await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
                pt: patientId,
                orId,
              })) as ConnectionOfMedcardResponseDto;

              const retrySorted = retryResponse.data.sort((a, b) => b.comid - a.comid);
              _comId = retrySorted[0]?.comid;
            }
          }
        }

        if (_comId) {
          setComId(_comId);
        }
      } catch (error) {
        console.error("Partograf getComId error:", error);
      }
    };

    fetchComId();
  }, [patientId, orId]);

  const {
    formData,
    handleChange,
    saveMasterRecord,
    traceData,
    fetalHeartRateData,
    serviksSizeData,
    headLevelData,
    addTraceRecord,
    updateTraceRecord,
    deleteTraceRecord,
    error,
    setError,
    getMCPParameterDetails,
    getMCPId,
    mcpCacheLoaded,
    initialLoading,
  } = usePartographForm({
    cardType: "PARTOGRAPH",
    recordId: undefined,
    user,
    comId: comId ?? 0,
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "warning" | "info" | "success">("success");
  const [genderWarningOpen, setGenderWarningOpen] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [traceForm, setTraceForm] = useState<any>({
    PROCESS_TIME: new Date(),
  });




  const handleTraceChange = (key: string, value: any) => {
    setTraceForm((prev: any) => ({ ...prev, [key]: value }));
  };

  // Resize listener for font size
  useEffect(() => {
    const handleResize = () => {
      setFontSize(window.innerWidth < 576 ? 10 : 14);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cinsiyet Kontrolü
  useEffect(() => {
    const checkPatientGender = async () => {
      if (!patientId) return;
      try {
        const patientInfo = await nurseService.patient.getPatientInfo<any>({ id: patientId });
        const gender = (patientInfo?.data?.sexuality || "").toUpperCase();
        const isFemale = ["KADIN", "K", "FEMALE", "F", "2"].includes(gender);

        if (gender && !isFemale) {
          setGenderWarningOpen(true);
        }
      } catch (err) {
        console.error("Gender check error:", err);
      }
    };
    checkPatientGender();
  }, [patientId]);

  // Options
  const prezentationOptions = useMemo(() => {
    const mcpId = getMCPId("PREZENTATION");
    if (!mcpId) return [];
    const details = getMCPParameterDetails(mcpId);
    return [...details]
      .sort((a, b) => (Number(a.orderNumber) || 0) - (Number(b.orderNumber) || 0) || Number(a.id) - Number(b.id))
      .map((d) => ({ value: d.id, label: d.name }));
  }, [getMCPId, getMCPParameterDetails, mcpCacheLoaded]);

  const painLevelOptions = useMemo(() => {
    const mcpId = getMCPId("PAIN_LEVEL");
    if (!mcpId) return [];
    return getMCPParameterDetails(mcpId).map((d) => ({ value: d.id, label: d.name }));
  }, [getMCPId, getMCPParameterDetails, mcpCacheLoaded]);

  const amnionFluidOptions = useMemo(() => {
    const mcpId = getMCPId("AMNION_FLUID");
    if (!mcpId) return [];
    return getMCPParameterDetails(mcpId).map((d) => ({ value: d.id, label: d.name }));
  }, [getMCPId, getMCPParameterDetails, mcpCacheLoaded]);

  const waterBladderOptions = useMemo(() => [
    { value: "1", label: t("partograf-form.there-is") },
    { value: "0", label: t("partograf-form.there-is-not") },
  ], [t]);

  const genderOptions = useMemo(() => [
    { value: "1", label: t("male") || "Erkek" },
    { value: "2", label: t("female") || "Kız" },
  ], [t]);

  const selectedGenderOption = genderOptions.find((opt) => opt.value === (formData as any).BABY_GENDER) || null;

  const painLevelRecords = useMemo(() => {
    return (traceData ?? [])
      .map((tr: any) => ({
        id: tr.ID || tr.id,
        count: Number(tr.PAIN_COUNT || 0),
        value: tr.PAIN_LEVEL_XVALUE || tr.PAIN_LEVEL || 0,
        name: tr.PAIN_LEVEL_XNAME || "",
        time: new Date(tr.PROCESS_TIME || tr.CTIME).getTime(),
      }))
      .sort((a, b) => a.time - b.time);
  }, [traceData]);

  // Alert
  const showAlert = (message: string, severity: "error" | "warning" | "info" | "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Handlers
  const handleSaveMedcard = async () => {
    try {
      if (!(formData as any).BABY_GENDER) {
        showAlert(t("validation.gender-required") || "Cinsiyet seçimi zorunludur!", "warning");
        return;
      }
      setIsProcessing(true);
      await saveMasterRecord();
      if (rowSelectionModel.length > 0) {
        const selectedId = rowSelectionModel[rowSelectionModel.length - 1];
        await updateTraceRecord(String(selectedId), traceForm);
      }
      setRowSelectionModel([]);
      setRowSelectionModel([]);
      setTraceForm({ PROCESS_TIME: new Date() });



      showAlert(t("save-success"), "success");
    } catch (error) {
      console.error("Hata:", error);
      showAlert(t("save-error"), "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddTrace = async () => {
    try {
      setIsProcessing(true);
      const result = await addTraceRecord({
        ...traceForm,
        PROCESS_TIME: traceForm.PROCESS_TIME || new Date(),
      });
      if (result) {
        showAlert(t("save-success"), "success");
        setIsAdding(false);
        setTraceForm({ PROCESS_TIME: new Date() });
        setRowSelectionModel([]);
      } else {
        showAlert(t("save-error"), "error");
      }
    } catch (error) {
      console.error("handleAddTrace error:", error);
      showAlert(t("save-error"), "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteMedcard = async () => {
    if (rowSelectionModel.length === 0) {
      showAlert(t("partograf-form.select-record-to-delete") || "Lütfen silinecek kaydı seçiniz", "warning");
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const selectedId = rowSelectionModel[rowSelectionModel.length - 1];
      if (selectedId) {
        await deleteTraceRecord(String(selectedId));
        showAlert(t("partograf-form.delete-success") || "Kayıt silindi", "success");
        setRowSelectionModel([]);
      }
    } catch (error) {
      console.error("Hata:", error);
      showAlert(t("partograf-form.delete-error") || "Silme işlemi sırasında hata oluştu", "error");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Data Grid Rows & Columns
  const traceRows: GridValidRowModel[] = useMemo(() => {
    return (traceData ?? []).map((trace: any, idx: number) => {
      const rawId = trace?.ID ?? trace?.id;
      return {
        id: rawId != null && rawId !== "" ? String(rawId) : `tmp-tr-${idx}`,
        _rawId: rawId != null && rawId !== "" ? String(rawId) : null,
        MPD_ID: trace?.MPD_ID,
        CUSER: trace?.CUSER,
        PROCESS_TIME: trace?.PROCESS_TIME,
        FETAL_HEART_RATE: trace?.FETAL_HEART_RATE,
        SERVIKS_SIZE: trace?.SERVIKS_SIZE,
        HEAD_LEVEL: trace?.HEAD_LEVEL,
        PAIN_LEVEL: trace?.PAIN_LEVEL_XNAME || trace?.PAIN_LEVEL,
        PAIN_COUNT: trace?.PAIN_COUNT,
        AMNION_FLUID: trace?.AMNION_FLUID_XNAME || trace?.AMNION_FLUID,
      };
    });
  }, [traceData]);

  const columns: GridColDef[] = [
    { field: "CUSER", headerName: t("partograf-form.nurse") || "Hemşire", flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "FETAL_HEART_RATE", headerName: t("partograf-form.fetal-heart-rate"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "SERVIKS_SIZE", headerName: t("partograf-form.cervix-dilation"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "HEAD_LEVEL", headerName: t("partograf-form.head-level"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "PAIN_LEVEL", headerName: t("partograf-form.pain-level"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "PAIN_COUNT", headerName: t("partograf-form.pain-count"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
    { field: "AMNION_FLUID", headerName: t("partograf-form.amniotic-fluid"), flex: 1, sortable: false, filterable: false, disableColumnMenu: true },
  ];

  const columns2: GridColDef[] = [
    { field: "date", headerName: t("partograf-form.columns.stat"), flex: 1 },
    { field: "field2", headerName: t("partograf-form.columns.time"), flex: 1 },
    { field: "field3", headerName: t("partograf-form.columns.date"), flex: 1 },
  ];

  const rows2: GridValidRowModel[] = useMemo(() => {
    const filteredRows = (traceData ?? [])
      .filter((tr: any) => tr?.AMNION_FLUID != null && String(tr.AMNION_FLUID) !== "")
      .map((tr: any, idx: number) => {
        const rawId = tr?.ID ?? tr?.id;
        const id = rawId != null && rawId !== "" ? String(rawId) : `tmp-tr-${idx}`;
        const amnionLabel =
          (tr as any).AMNION_FLUID_XNAME ||
          (tr as any).AMNION_FLUID_XVALUE ||
          amnionFluidOptions.find((opt) => String(opt.value) === String(tr.AMNION_FLUID))?.label ||
          String(tr.AMNION_FLUID ?? "");
        const processTime = tr.PROCESS_TIME || tr.CTIME;
        return {
          id,
          date: amnionLabel,
          field2: formatTime(processTime),
          field3: formatDate(processTime),
        };
      });

    if (filteredRows.length === 0) {
      return [{ id: "empty-amnion-row", date: "", field2: "", field3: "" }];
    }
    return filteredRows;
  }, [traceData, amnionFluidOptions]);

  return (
    <>
      <Stack sx={{ padding: "5px", marginBottom: "20px" }} mt={5} direction="row" spacing={2}>
        <ByzNurseSaveButton onClick={handleSaveMedcard} />
        <ByzMuiAddButton onClick={handleAddTrace} />
        <ByzNurseDeleteButton onClick={handleDeleteMedcard} disabled={rowSelectionModel.length === 0} />
        <ByzNursePrintButton2 />
      </Stack>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <PartographBornInfo
          formData={formData}
          handleChange={handleChange}
          prezentationOptions={prezentationOptions}
          waterBladderOptions={waterBladderOptions}
          genderOptions={genderOptions}
          selectedGenderOption={selectedGenderOption}
        />

        <PartographTraceGrid
          columns={columns}
          traceRows={traceRows}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => {
            setRowSelectionModel(newSelection);
            setIsAdding(false);
            if (newSelection.length > 0) {
              const selectedId = newSelection[newSelection.length - 1];
              const originalTrace = (traceData || []).find((t: any) => String(t.ID || t.id) === String(selectedId));
              if (originalTrace) {
                setTraceForm({
                  PROCESS_TIME: originalTrace.PROCESS_TIME ? new Date(originalTrace.PROCESS_TIME) : null,
                  FETAL_HEART_RATE: originalTrace.FETAL_HEART_RATE ?? "",
                  SERVIKS_SIZE: originalTrace.SERVIKS_SIZE ?? "",
                  HEAD_LEVEL: originalTrace.HEAD_LEVEL ?? "",
                  PAIN_LEVEL: originalTrace.PAIN_LEVEL ?? "",
                  PAIN_COUNT: originalTrace.PAIN_COUNT ?? "",
                  AMNION_FLUID: originalTrace.AMNION_FLUID ?? "",
                });
              }
            } else {
              setTraceForm({
                PROCESS_TIME: new Date(),
                FETAL_HEART_RATE: "",
                SERVIKS_SIZE: "",
                HEAD_LEVEL: "",
                PAIN_LEVEL: "",
                PAIN_COUNT: "",
                AMNION_FLUID: "",
              });
            }
          }}
        />

        <PartographParameters
          traceForm={traceForm}
          handleTraceChange={handleTraceChange}
          rowSelectionModel={rowSelectionModel}
          isAdding={isAdding}
          painLevelOptions={painLevelOptions}
          amnionFluidOptions={amnionFluidOptions}
        />

        <PartographCharts
          serviksSizeData={serviksSizeData}
          fetalHeartRateData={fetalHeartRateData}
          headLevelData={headLevelData}
          fontSize={fontSize}
        />

        <PartographBottomGrids
          columns2={columns2}
          rows2={rows2}
          painLevelRecords={painLevelRecords}
        />
      </Box>

      <PartographDialogs
        error={error}
        setError={setError}
        alertOpen={alertOpen}
        setAlertOpen={setAlertOpen}
        alertSeverity={alertSeverity}
        alertMessage={alertMessage}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        handleConfirmDelete={handleConfirmDelete}
        genderWarningOpen={genderWarningOpen}
        setGenderWarningOpen={setGenderWarningOpen}
        onCloseModal={onCloseModal}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={isProcessing || !comId || initialLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default PartografForm;
